/**
 * Générateur — Automatisme : écritures fractionnaires ↔ décimales usuelles
 * Attendu ID : 1012 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025).
 * Labels (cartes exerciseur) :
 *   - Fractions usuelles et écritures décimales (1/4 = 0,25...)
 *
 * ── Plan de cartes (2026-08-20) ────────────────────────────────────────────
 * UNE carte, 5 formats. Domaine FERMÉ : les SIX faits du programme, rien
 * d'autre (« ce sont six faits, pas une procédure » — aucun 7/4 ni 1/8,
 * frontière A5 stricte) :
 *   1/4 = 0,25 ; 1/2 = 0,5 ; 3/4 = 0,75 ; 3/2 = 1,5 ; 4/2 = 2 ; 5/2 = 2,5
 * DEUX FAMILLES aux mécaniques distinctes (analyse didactique 2026-08-20),
 * mêlées dans chaque tirage multi-items et exposées via _meta :
 *   - quarts_demis (< 1) : numération — 25, 50, 75 centièmes (la monnaie) ;
 *   - demi_entiers : sens quotient — la moitié de 3, de 4, de 5 (4/2 = 2
 *     rappelle qu'une fraction peut désigner un ENTIER).
 * INVARIANTS :
 *   - chaque égalité émise est RE-VÉRIFIÉE numériquement (throw) ;
 *   - saisie : réponse décimale tapée telle quelle ; réponse FRACTION via
 *     trou-numérateur ou trou-dénominateur (l'autre affiché) — jamais de
 *     « 3/4 » à taper (parade du 1011), réponse unique dans tous les cas ;
 *   - QCM : distracteur-phare VIRGULE_COLLEE (3/4 → « 3,4 ») + valeurs du
 *     domaine, distincts de la clé en texte ET en valeur ;
 *   - relier (premier de la série) : fractions ↔ décimaux, avec un leurre
 *     virgule_collee dans les cibles ;
 *   - V/F : lignes fausses re-vérifiées FAUSSES numériquement.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, tex,
} from '../../../utils';

const ATTENDU_ID = 1012;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Fractions usuelles et écritures décimales (1/4 = 0,25...)';

// ─── Les SIX faits (domaine fermé) ─────────────────────────────────────────

const FAITS = [
    { n: 1, d: 4, dec: '0,25', famille: 'quarts_demis' },
    { n: 1, d: 2, dec: '0,5', famille: 'quarts_demis' },
    { n: 3, d: 4, dec: '0,75', famille: 'quarts_demis' },
    { n: 3, d: 2, dec: '1,5', famille: 'demi_entiers' },
    { n: 4, d: 2, dec: '2', famille: 'demi_entiers' },
    { n: 5, d: 2, dec: '2,5', famille: 'demi_entiers' },
];

const fracTex = (f) => `\\dfrac{${f.n}}{${f.d}}`;
const fracBrut = (f) => `${f.n}/${f.d}`;
const decTex = (f) => f.dec.replace(',', '{,}');
const valDec = (s) => Number(String(s).replace(',', '.'));

/** AUTO-VÉRIFICATION : le fait affiché doit être numériquement vrai. */
const verifierFait = (f) => {
    if (Math.abs(f.n / f.d - valDec(f.dec)) > 1e-12) {
        throw new Error(`gen_1012 : fait faux (${f.n}/${f.d} = ${f.dec})`);
    }
    return f;
};

/**
 * Tire `nb` faits distincts en MÊLANT les deux familles (au moins un de
 * chaque dès que nb ≥ 2). Boucle bornée, repli déterministe.
 */
const tirerFaits = (nb) => {
    for (let essai = 0; essai < 40; essai++) {
        const pris = shuffle(FAITS).slice(0, nb).map(verifierFait);
        const familles = new Set(pris.map((f) => f.famille));
        if (nb < 2 || familles.size === 2) return pris;
    }
    return FAITS.slice(0, nb).map(verifierFait);
};

/** item = { fait, versDec: bool, sens: 0|1 } — versDec : le trou/la question
 *  porte sur l'écriture décimale ; sinon sur la fraction. */
const texEgalite = (item, slot = null) => {
    const f = item.fait;
    let gauche = fracTex(f);
    let droite = decTex(f);
    if (slot === 'trou') {
        if (item.versDec) droite = `{{${f.dec}}}`;
        else if (item.trouFraction === 'num') gauche = `\\dfrac{{{${f.n}}}}{${f.d}}`;
        else if (item.trouFraction === 'den') gauche = `\\dfrac{${f.n}}{{{${f.d}}}}`;
        else gauche = `{{${fracBrut(f)}}}`;
    } else if (slot === 'question') {
        if (item.versDec) droite = '?';
        else gauche = '?';
    }
    return item.sens === 0 ? `${gauche} = ${droite}` : `${droite} = ${gauche}`;
};

// ─── Format 1 : texte à trous DRAG ────────────────────────────────────────

const genUsuellesTrouDrag = () => {
    const faits = tirerFaits(3);
    const items = faits.map((fait) => ({
        fait, versDec: Math.random() < 0.5, sens: randInt(0, 1),
    }));
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    // Tuiles pièges : une valeur du domaine non utilisée (décimale ou
    // fraction selon les mots présents) + la virgule collée d'un des faits.
    const mots = new Set(items.map((it) => (it.versDec ? it.fait.dec : fracBrut(it.fait))));
    const restants = FAITS.filter((f) => !faits.includes(f));
    const faitCollee = randomChoice(faits);
    const candidats = shuffle([
        ...restants.map((f) => f.dec),
        `${faitCollee.n},${faitCollee.d}`,
    ]).filter((c) => !mots.has(c) && !faits.some((f) => Math.abs(valDec(c) - f.n / f.d) < 1e-12));
    const vus = new Set();
    const distracteurs = candidats.filter((c) => {
        const v = valDec(c);
        if (vus.has(v)) return false;
        vus.add(v);
        return true;
    }).slice(0, 2);
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 2 : texte à trous SAISIE ──────────────────────────────────────

const genUsuellesTrouSaisie = () => {
    const faits = tirerFaits(4);
    const items = faits.map((fait) => {
        const versDec = Math.random() < 0.55;
        return {
            fait,
            versDec,
            trouFraction: versDec ? null : (Math.random() < 0.5 ? 'num' : 'den'),
            sens: randInt(0, 1),
        };
    });
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : QCM (cf. REGLES_DISTRACTEURS_QCM) ─────────────────────────

const genUsuellesQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const fait = verifierFait(randomChoice(FAITS));
        const versDec = Math.random() < 0.5;
        let choix;
        let candidats;
        let texte;
        if (versDec) {
            texte = `<p><b>Quelle est l'écriture décimale de ${tex(fracTex(fait))} ?</b></p>`;
            candidats = [
                { valeur: `${fait.n},${fait.d}`, code: 'virgule_collee' },
                ...shuffle(FAITS.filter((f) => f !== fait)).slice(0, 2)
                    .map((f) => ({ valeur: f.dec, code: 'autre_fait_du_domaine' })),
            ];
            choix = [fait.dec, ...candidats.map((c) => c.valeur)];
        } else {
            texte = `<p><b>Quelle fraction est égale à ${tex(decTex(fait))} ?</b></p>`;
            candidats = [
                { valeur: `$\\dfrac{${fait.d}}{${fait.n}}$`, code: 'fraction_inversee' },
                ...shuffle(FAITS.filter((f) => f !== fait)).slice(0, 2)
                    .map((f) => ({ valeur: `$${fracTex(f)}$`, code: 'autre_fait_du_domaine' })),
            ];
            choix = [`$${fracTex(fait)}$`, ...candidats.map((c) => c.valeur)];
        }
        if (new Set(choix).size !== 4) continue;
        // Garantie A1 : aucun distracteur n'a la valeur de la clé (la
        // fraction inversée de 1/2 vaut 2 = un autre fait, mais jamais la clé).
        const valDe = (c) => {
            const m = String(c).match(/\\dfrac\{(\d+)\}\{(\d+)\}/);
            return m ? Number(m[1]) / Number(m[2]) : valDec(c);
        };
        if (candidats.some((c) => Math.abs(valDe(c.valeur) - fait.n / fait.d) < 1e-12)) continue;

        const payload = makeQcm(choix, choix[0], texte, opts(L1), false);
        payload.contenu = { ...payload.contenu, sousType: fait.famille };
        payload._meta.params = { ...fait, versDec, distracteurs: candidats };
        return payload;
    }
    const fait = FAITS[0];
    const payload = makeQcm(['0,25', '1,4', '0,5', '2'], '0,25',
        `<p><b>Quelle est l'écriture décimale de ${tex(fracTex(fait))} ?</b></p>`, opts(L1), false);
    payload.contenu = { ...payload.contenu, sousType: fait.famille };
    payload._meta.params = { ...fait, versDec: true, distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

const fausserFait = (fait, sens) => {
    const candidats = [
        `${fait.n},${fait.d}`,                                     // virgule collée
        ...FAITS.filter((f) => f !== fait).map((f) => f.dec),      // autre fait
    ].filter((c) => Math.abs(valDec(c) - fait.n / fait.d) > 1e-12);
    const faux = randomChoice(candidats);
    if (Math.abs(valDec(faux) - fait.n / fait.d) < 1e-12) {
        throw new Error('gen_1012 : la ligne censée être fausse est vraie');
    }
    const g = fracTex(fait);
    const d = faux.replace(',', '{,}');
    return sens === 0 ? `${g} = ${d}` : `${d} = ${g}`;
};

const genUsuellesVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const faits = tirerFaits(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = faits.map((fait, i) => {
            const sens = randInt(0, 1);
            return etiq[i] === 0
                ? `$${texEgalite({ fait, versDec: true, sens })}$`
                : `$${fausserFait(fait, sens)}$`;
        });
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : relier (fractions ↔ décimaux) ─────────────────────────────

const genUsuellesRelier = () => {
    const faits = tirerFaits(4);
    // Sources en fractions EMPILÉES : toute la chaîne du relier (élève,
    // corrigé, print) rend le MathText — pas de contrainte {{mot}} ici,
    // contrairement au drag (arbitrage 2026-08-20).
    const pairs = faits.map((f) => ({ source: `$${fracTex(f)}$`, target: f.dec }));
    // Un leurre NOMMÉ : la virgule collée d'une des fractions reliées —
    // jamais égal à une cible légitime (vérifié numériquement).
    const leurres = [];
    for (const f of shuffle(faits)) {
        const collee = `${f.n},${f.d}`;
        if (!FAITS.some((x) => Math.abs(valDec(collee) - x.n / x.d) < 1e-12)) {
            leurres.push({ target: collee, code: 'virgule_collee' });
            break;
        }
    }
    return makeRelier(
        pairs,
        '<p><b>Relie chaque fraction à son écriture décimale.</b></p>',
        { ...opts(L1), cibles_leurres: leurres },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'le domaine est FERMÉ (les six faits du programme) : les distracteurs sont la virgule collée, la fraction inversée et les autres faits — jamais une fraction hors liste',
        'chaque distracteur est vérifié différent de la clé en texte ET en valeur',
    ],
    usuelles_qcm_unique: {
        support: 'un des six faits, dans un sens ou dans l\'autre (contenu.sousType = la famille didactique)',
        fixes: {
            virgule_collee: 'LE piège : numérateur virgule dénominateur (3/4 → « 3,4 »)',
            fraction_inversee: 'vers_fraction : dénominateur et numérateur échangés (0,5 → 2/1)',
            autre_fait_du_domaine: 'un autre des six faits : confusion de mémorisation',
        },
    },
    usuelles_relier: {
        support: '4 paires fraction ↔ décimal',
        fixes: {
            virgule_collee: 'cible surnuméraire « n,d » — aucune source ne lui correspond',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    usuelles_texte_trou_drag: genUsuellesTrouDrag,
    usuelles_texte_trou_saisie: genUsuellesTrouSaisie,
    usuelles_qcm_unique: genUsuellesQcm,
    usuelles_tableau_vf: genUsuellesVraiFaux,
    usuelles_relier: genUsuellesRelier,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', relier: 'Relier',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'usuelles', label: L1, formatsList: buildFormats('usuelles') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
